import React, {useEffect} from 'react';
import {Box} from '@mui/material';
import SourceDestination from '../CreateRecommend/RecommendSourceDestination/SourceDestination';
import RecommendPlanForm, {
  RecommendPlanFormsStyled,
} from '../CreateRecommend/RecommendSourceDestination/RecommendPlanForm';
import {RecommendProps} from '../CreateRecommend/RecommendSourceDestination/validators';
import {getSummary} from '../CreateRecommend/RecommendSourceDestination/naming';
import RecommendParserStep from '../RecommendParserStep/RecommendParserStep';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import {modelPortfolioField, modifyFieldsType} from './modifyFieldTypes';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {useResetReccs} from '../CreateRecommend/RecommendSourceDestination/useResetReccs';
import {useRecommendContext} from '../CreateRecommend/CreateRecommendHelpers';
import {PotDetails} from '../../../schema-types/PotDetails';
import {ModifyFields} from './ModifyFields';
import {Pot, Pot_children} from '../../../schema-types/Pot';
import {
  useAutoForm,
  useAutoField,
  useLoadedAutoField,
} from '../../../components/AutoForm/useAutoForm';
import {UPDATES} from '../../../shared/UPDATES';
import {recommendNoteToFields, modifyChanges} from '../hooks/modifyHooks';

export {recommendNoteToFields, modifyChanges};

// export const changeSourceFactory = (
//   modifySourceHelpers: FieldHelperProps<
//     | {
//         id: string;
//       }
//     | undefined
//   >,
//   setFieldValue: FormikHelpers<recommendFieldsType>['setFieldValue']
// ) => {
//   return (event: React.ChangeEvent<any> | null, value: string) => {

// };

export const RecommendModify: React.FC<RecommendProps> = ({
  sourcePots,
  sourceDestinationLabels,
}) => {
  const {loaded, setLoaded} = useLoadedAutoField();
  const {values, setFieldValue} = useAutoForm<recommendFieldsType>();
  useResetReccs(OVRecommendActionEnum.MODIFY);
  const {recommendDetails, recommendReviewId} = useRecommendContext();
  const [modifyFieldObj, modifyMeta, modifyHelpers] =
    useAutoField<recommendFieldsType['modify']>('modify');
  const [modifySourceObj, modifySourceMeta, modifySourceHelpers] = useAutoField<
    modifyFieldsType['source'] | undefined
  >('modify.source');
  useEffect(() => {
    modifyHelpers.setValue(UPDATES._660, {
      feeChange: false,
      mandateChange: false,
      regularContributionChange: false,
      regularWithdrawalChange: false,
      changeOfOwnership: false,
      note: '',
      source: {
        id: '',
      },
      model: undefined,
    });
  }, []);
  const sources = sourcePots.reduce<PotDetails[]>((acc, parentPot) => {
    acc.push(parentPot);
    if (parentPot.children) {
      parentPot.children.forEach((childPot) => {
        acc.push(childPot);
      });
    }
    return acc;
  }, []);

  const changeSource = (event: React.ChangeEvent<any> | null, value: string) => {
    const id = value || event?.target?.value;
    modifySourceHelpers.setValue(UPDATES._662, {
      id,
    });
  };

  useEffect(() => {
    const id = values.modify?.source?.id;
    const source = sourcePots.find((x) => x.id === id);
    if (source) {
      setFieldValue(UPDATES._650, `modify.planProvider`, source?.productProvider?.id);
      setFieldValue(UPDATES._651, `modify.planType`, source?.productName?.id);
      setFieldValue(
        UPDATES._652,
        `modify.existingModel`,
        source?.modelPortfolio || source.mandate
      );
    } else {
      setFieldValue(UPDATES._653, `modify.planProvider`, undefined);
      setFieldValue(UPDATES._654, `modify.planType`, undefined);
      setFieldValue(UPDATES._655, `modify.existingModel`, undefined);
    }
  }, [values.modify?.source]);

  useEffect(() => {
    if (!recommendDetails) {
      // do nothing
      setLoaded();
      return;
    }
    const newFields = recommendNoteToFields(recommendDetails, sourcePots);
    modifyHelpers.setValue(UPDATES._661, newFields);
    setLoaded();
  }, [recommendDetails]);

  useEffect(() => {
    /**
     * This updates source fields used by validation
     * DO NOT DELETE THEM
     */
    sourcePots.forEach((parent) => {
      const sourceID = modifyFieldObj.value?.source.id;
      if (parent.id === sourceID) {
        modifySourceHelpers.setValue(UPDATES._662, {
          id: sourceID!,
          isChild: false,
          isSaltus: !!parent.isSaltus,
        });
      }
      parent.children?.forEach((child) => {
        if (child.id === sourceID) {
          modifySourceHelpers.setValue(UPDATES._662, {
            id: sourceID!,
            isChild: true,
            isSaltus: !!parent.isSaltus,
          });
        }
      });
    });
  }, [modifyFieldObj.value?.source.id]);
  const sourcePot: Pot_children | undefined = sourcePots.reduce<Pot_children | undefined>(
    (_, x) => {
      if (x.id === modifyFieldObj.value?.source.id) {
        return x;
      }
      const childPot = x.children?.find((y) => y.id === modifyFieldObj.value?.source.id);

      if (childPot) {
        return childPot;
      }
      return _;
    },
    undefined
  );

  return sourcePots?.length === 0 ? (
    <RecommendParserStep errorText="No plans to modify" />
  ) : (
    <SourceDestination
      toError={modifyMeta.error}
      // toError={modifyMeta.error}
      fromError={modifySourceMeta.error}
      source={
        <RecommendPlanFormsStyled>
          {sources.map((el) => (
            <RecommendPlanForm
              key={el.id}
              id={el.id}
              sameReview={el.review?.id === recommendReviewId}
              reccId={el.originalOvrId}
              indent={!!el.parent?.id}
              summary={
                <Box display="flex" flexDirection="row">
                  {getSummary(el)}
                </Box>
              }
              details={false}
              isSelected={modifyFieldObj.value?.source.id === el.id}
              isSubplan={false}
              handleChange={changeSource}
            />
          ))}
        </RecommendPlanFormsStyled>
      }
      destination={<ModifyFields sourcePot={sourcePot} />}
      sourceLabel={sourceDestinationLabels?.first}
      destinationLabel="Changes to make"
    />
  );
};

export default RecommendModify;
